import React, { useState } from "react";
import moment from "moment";
import ReactStars from "react-rating-stars-component";
import Avatar from "react-avatar";
import Constants from "../utils/Constants";
import { isMissedCallHistoryItem, msToHMSShort, getCallOutcomeLabel } from "../utils/validation";
import missedCallIcon from "../assets/img/missed-call.svg";

const getDisplayMeta = (item, isSrmIpAgent) => {
    const isNonSrmIp = Number(isSrmIpAgent) === 0;
    const patientName = isNonSrmIp
        ? (item?.User?.fullName || "Unknown patient")
        : (
            item?.SrmIpRoomServiceMapping?.SrmIpRoom?.patientRoomName
            || item?.User?.fullName
            || "Unknown patient"
        );
    const roomId = isNonSrmIp ? "" : (item?.SrmIpRoomServiceMapping?.SrmIpRoom?.patientRoomId || "");
    const letterWords = patientName
        .replace(/[^a-zA-Z\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    const avatarName = letterWords.length > 0 ? letterWords.join(" ") : "Patient";
    // Prefer full Room ID on avatar (same as mobile); fall back to name initials
    const avatarLabel = roomId ? String(roomId) : null;

    return { patientName, roomId, avatarName, avatarLabel };
};

const StoreHistoryItem = ({ item, isSrmIpAgent, showEscalationTimeline = false }) => {
    const [expanded, setExpanded] = useState(false);
    const isMissed = isMissedCallHistoryItem(item);
    const outcomeLabel = getCallOutcomeLabel(item);
    const { patientName, roomId, avatarName, avatarLabel } = getDisplayMeta(item, isSrmIpAgent);
    const relativeTime = item?.createdAt ? moment(item.createdAt).fromNow() : "-";
    const timeRange = item?.updatedAt
        ? `${moment(item.createdAt).format("DD MMM YYYY, hh:mm A")} – ${moment(item.updatedAt).format("hh:mm A")}`
        : moment(item?.createdAt).format("DD MMM YYYY, hh:mm A");
    const showSrmTier = Number(isSrmIpAgent) !== 0;
    const tierLabel = item?.srmEscalationTierLabel || "";
    const rungNurseName = item?.RungNurseName || item?.AttendedByUser?.fullName || "";
    const timeline = Array.isArray(item?.escalationTimeline) ? item.escalationTimeline : [];
    const canShowTimeline = !!(showEscalationTimeline && item?.canViewEscalationTimeline && timeline.length > 0);
    const missedRoomNurse = item?.missedRoomNurse;
    const canShowMissedRoom = !!(
        showEscalationTimeline
        && item?.canViewMissedRoomNurse
        && missedRoomNurse
        && (missedRoomNurse.missed || missedRoomNurse.notAssigned)
    );
    const hasCustomerComment = !!(item?.feedback && String(item.feedback).trim());
    const canExpand = !!(hasCustomerComment || canShowTimeline || canShowMissedRoom);

    return (
        <div className={`store-history-card ${isMissed ? "store-history-card--missed" : ""}`}>
            <div className="store-history-card__main">
                {avatarLabel ? (
                    <div
                        className={`store-history-card__avatar ${String(avatarLabel).length > 3 ? "store-history-card__avatar--compact" : ""}`}
                        aria-hidden="true"
                    >
                        {avatarLabel}
                    </div>
                ) : (
                    <Avatar
                        name={avatarName}
                        maxInitials={2}
                        round
                        size="44px"
                        color="var(--nurse-icon-bg)"
                        fgColor="var(--nurse-icon-txt)"
                    />
                )}
                <div className="store-history-card__body">
                    <p className="store-history-card__name">{patientName}</p>
                    {roomId ? <p className="store-history-card__room">Room ID: {roomId}</p> : null}
                    {showSrmTier && (tierLabel || rungNurseName) ? (
                        <p className="store-history-card__nurse-tier">
                            {[tierLabel, rungNurseName].filter(Boolean).join(": ")}
                        </p>
                    ) : null}
                    {canShowMissedRoom && !canShowTimeline ? (
                        <p className="store-history-card__missed-prior">
                            Room Nurse: {missedRoomNurse.name} · {missedRoomNurse.atIst || "-"}
                        </p>
                    ) : null}
                    {isMissed ? (
                        <div className="store-history-card__missed">
                            <img src={missedCallIcon} alt="" className="missed-call" />
                            <span>{outcomeLabel}</span>
                        </div>
                    ) : (
                        <p className="store-history-card__duration">
                            Call duration: {msToHMSShort(item?.callDuration)}
                        </p>
                    )}
                    <p className="store-history-card__time" title={timeRange}>{relativeTime}</p>
                    {item?.ratings > 0 && (
                        <ReactStars
                            count={5}
                            edit={false}
                            size={18}
                            value={item.ratings}
                            isHalf
                            emptyIcon={<i className="far fa-star" />}
                            halfIcon={<i className="fa fa-star-half-alt" />}
                            fullIcon={<i className="fa fa-star" />}
                            activeColor="#ffd700"
                        />
                    )}
                </div>
                {canExpand ? (
                    <button
                        type="button"
                        className={`store-history-card__expand ${expanded ? "is-open" : ""}`}
                        onClick={() => setExpanded(!expanded)}
                        aria-expanded={expanded}
                        aria-label="Toggle details"
                    >
                        <i className="fa-solid fa-chevron-down" />
                    </button>
                ) : null}
            </div>
            {expanded ? (
                <div className="store-history-card__feedback">
                    {canShowTimeline ? (
                        <div className="store-history-card__timeline">
                            <p className="store-history-card__feedback-label">Escalation timeline</p>
                            {item?.escalationSessionStartedAtIst ? (
                                <p className="store-history-card__timeline-meta">
                                    Call started: {item.escalationSessionStartedAtIst}
                                </p>
                            ) : null}
                            {timeline.map((leg, index) => (
                                <div
                                    key={`timeline-${leg.appointmentId || index}`}
                                    className="store-history-card__timeline-row"
                                >
                                    <p className="store-history-card__timeline-event">
                                        {leg.eventLabel || leg.tierLabel}
                                    </p>
                                    <p className="store-history-card__timeline-nurse">
                                        {(leg.tierLabel || "Nurse")}: {leg.assignedNurseName || leg.rungNurseName || "Not assigned"}
                                    </p>
                                    <p className="store-history-card__timeline-time">{leg.atIst || "-"}</p>
                                    <p className={leg.missed || leg.notAssigned
                                        ? "store-history-card__timeline-missed"
                                        : "store-history-card__timeline-ok"}
                                    >
                                        {leg.notAssigned
                                            ? "Not assigned"
                                            : (leg.rejected
                                                ? "Rejected"
                                                : (leg.missed ? "Not answered" : (leg.callOutcomeLabel || "Answered")))}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : canShowMissedRoom ? (
                        <div className="store-history-card__timeline">
                            <p className="store-history-card__feedback-label">Missed Room Nurse</p>
                            <p className="store-history-card__timeline-nurse">
                                {missedRoomNurse.name} · {missedRoomNurse.atIst || "-"}
                            </p>
                            <p className="store-history-card__timeline-missed">
                                {missedRoomNurse.notAssigned ? "Not assigned" : "No answer"}
                            </p>
                        </div>
                    ) : null}
                    {hasCustomerComment ? (
                        <>
                            <p className="store-history-card__feedback-label">{Constants.COMMENTS}</p>
                            <p className="store-history-card__feedback-text">{String(item.feedback).trim()}</p>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default StoreHistoryItem;
